import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Form, Input, Button, Tabs, Modal, message, notification } from 'antd';
import * as _ from 'lodash';
import { Icon } from 'dlweb-components';
import { v4 as uuidv4 } from 'uuid';
import ProfileProperties from '../../ProfileProperties';
import WorkflowForm from '../../WorkflowForm';
import NotificationForm from '../../NotificationForm';
import AdvancedOptionsForm from '../../AdvancedOptionsForm';
import Dropzone from '../../Dropzone';
import profileService from '../../../services/profileService';
import FileList from './FileList';
import useFileUpload from '../../../hooks/useFileUpload';
import { fileService } from '../../../services/fileService';

const Import = ({maxFileSize, angularScope, files, importFolder, modalProps, modalOptions, responsiveProps, modalRef, closeModal}) => {

  const [form] = Form.useForm();  

  const inputRef = useRef(null);

  const [disabled, setDisabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [processing, setProcessing] = useState(false);
  const [userInputValue, setUserInputValue] = useState('');
  const [folderSettings, setFolderSettings] = useState({});
  const [importFiles, setImportFiles] = useState([]);
  const [currentPropertyValues, setCurrentPropertyValues] = useState([]);

  const handleUploadsComplete = async (batch) => {

    let _batch = { ...batch };

    _batch.properties = batch.properties.map(p => ({ name: p.name, value: p.value }));

    setStatus('Importing files...');

    const controller = new AbortController();
    const signal = controller.signal;
  
    window.currentAbortController = controller;
    window.currentAbortController.batchId = batch.batchId;
    
    try {
      const { data: response } = await axios.post(
        '/api/wt/documents/importFiles',
        { ..._batch },
        { signal } // Pass the signal to the request
      );
  
      if (response.errors.length > 0) {
        console.error('Error importing files:', response.errors[0]);
        notification.error({ message: 'File could not be imported' });
      } else {
        angularScope.$broadcast('reloadGrid');
        notification.success({ message: 'Files imported successfully' });
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        console.warn('Import request canceled:', error.message);
      } else {
        console.error('Error importing files:', error);
        notification.error({ message: 'An error occurred during import' });
      }
    } finally {
      closeModal();
      Modal.destroyAll(); // just in case the confirmation modal is still open
    }
  };

  const { uploadFiles, cancelUpload, pauseUpload, resumeUpload, uploadProgress, isUploading, cancelCleanup } = useFileUpload(handleUploadsComplete);

  const cancelImport = () => {
    if (window.currentAbortController) {
      window.currentAbortController.abort();
      cancelCleanup(window.currentAbortController.batchId);
      window.currentAbortController = null;
    }
  };

  useEffect(() => {
    setDisabled(importFiles.length === 0 || currentPropertyValues.properties?.length === 0);
  }, [importFiles, currentPropertyValues])

  useEffect(() => files && setImportFiles([...files]), [files])
  useEffect(() => {
    const setFormValues = async () => {
      const fs = await axios.get(`/api/wt/documents/import/settings/read?folderId=${importFolder.id}`);
      setFolderSettings(fs.data.data);
    }
    setFormValues();
  }, [importFolder])

  const handleSubmit = async (forms) => {

    const { profileName, properties } = currentPropertyValues;
    const { mainForm, notificationForm, workflowForm, advancedOptionsForm } = forms;

		// validate required properties before we call the server
    const validateRequiredProperties = await profileService.validateRequiredProperties(properties);
		if(!validateRequiredProperties) return;

    setLoading(true);
    setProcessing(true);
    setStatus('Uploading files...');

    const uploadBatch = {
      batchId: uuidv4(),
      files: importFiles,
      meta: {
        folderID: importFolder.id,
        profileName,
        properties,
        ...mainForm.getFieldsValue(),
        ...notificationForm.getFieldsValue(),
        ...workflowForm.getFieldsValue(),
        ...advancedOptionsForm.getFieldsValue()
      }
    }

    profileService.validatePropertyValues(profileName, properties).then(res => {
      const responseMessage = res.data.message;
      if(responseMessage.length > 0) {
        message.error(responseMessage);
        setLoading(false);
        setProcessing(false);
        return;
      }
      else {
        uploadFiles(uploadBatch);
      }
    })
  }

  const showLocalFilePicker = async () => {

    try {
      const pickerOptions = {
        multiple: true,
        excludeAcceptAllOption: false
      };
  
      const fileHandle = await fileService.openFileBrowser(pickerOptions);
      const files = await fileHandle.getFile();
      
      for (const file of files) {
        file.relativePath = fileHandle.relativePath || file.name;
        if (file.size > maxFileSize * 1024) {
          message.error(
            <span>
              <strong>{file.name}</strong> exceeds maximum allowed size.
            </span>
          );
          return;
        }
      }

      onFileDrop(files);
    }
    catch (err) {
      // do nothing - we get an error when the use cancels the file picker
    }
  }

  const checkUnique = (currentList, newList) => {
    currentList.forEach((currentFile) => {
      newList.forEach((newFile) => {
        if (currentFile.name === newFile.name && currentFile.relativePath === newFile.relativePath) {
          // message.error(`Duplicate file found: ${newFile.name}`);
        }
      })
    })
  }

  const onFileDrop = (files) => {

    setImportFiles(lf => {
    
      checkUnique(lf, files);

      const newFileList = _.uniqBy([...lf, ...files], o => `${o.relativePath}${o.name}`);

      return newFileList;
    });
  }

  const onClose = () => {
    if(processing) {
      pauseUpload();
      Modal.confirm(
        {
          okText: "Yes",
          cancelText: "No",
          title: "Import in progress",
          content: "Are you sure you want to cancel the import?",
          onCancel: () => { 
            resumeUpload()
          },
          onOk: () => {
            if(isUploading) {
              cancelUpload();
            }
            else {
              cancelImport();
            }
            closeModal();
          },
        }
      )
    }
    else {
      closeModal();
    }
  }

  return (
    <Modal
      innerRef={modalRef}
      {...modalOptions}
      {...modalProps}
      {...responsiveProps}
      onCancel={onClose}
    >
      <Form.Provider
        onFormFinish={(name, { forms }) => handleSubmit(forms)}
      >
      <Dropzone onFileDrop={onFileDrop}>
        <Container id="import-container" className="wide-modal-container">
          {processing && 
            <ProcessingOverlay />
          }
          <div className="wide-modal-left">
            <Form
              name="mainForm"
              form={form}
              layout='vertical'
              labelAlign="left"
              initialValues={{description: '', versionNotes: ''}}
            >
              <div style={{height: '100%', width: '100%', display: 'flex', flexDirection: 'column', paddingBottom: '10px', position: 'relative'}}>
                <Button size="small" style={{position: 'absolute', top: '3px', right: '0px', color: 'var(--primary)', zIndex: '1'}} type="text" onClick={showLocalFilePicker}>+ Add files</Button> 
                <Form.Item label={`Importing ${importFiles.length || 0} document${importFiles.length === 1 ? '': 's  '}`} style={{flex: '40% 1', width: '100%', overflow: 'hidden'}} className="flex-item flex-item-list">
                  <FileList
                    files={importFiles}
                    processing={processing}
                    isUploading={isUploading}
                    fileProgress={uploadProgress}
                    showLocalFilePicker={showLocalFilePicker}
                    addFile={file => setImportFiles([...importFiles, file])}
                    removeFile={file => setImportFiles(importFiles.filter(f => f !== file))}
                  />
                </Form.Item>

                <>
                  <Form.Item label="Description" name="description" style={{flex: 1, padding: '0', margin: 0, maxHeight: '300px'}} className="flex-item">
                    <Input.TextArea
                      ref={inputRef}
                      value={userInputValue}
                      maxLength={250}
                      onChange={(e) => { setUserInputValue(e.target.value)} }
                      style={{resize: 'none', height: "80%"}}
                    />
                  </Form.Item>
                  <Form.Item label="Version notes" name="versionNotes" style={{flex: 1, padding: '0', margin: 0, maxHeight: '300px'}} className="flex-item">
                    <Input.TextArea
                      ref={inputRef}
                      maxLength={1000}
                      value={userInputValue}
                      onChange={(e) => { setUserInputValue(e.target.value)} }
                      style={{resize: 'none', height: "80%"}}
                    />
                  </Form.Item>
                </>
              </div>
            </Form>
          </div>
          <div  className="wide-modal-right">
            {processing &&
              <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10}}>
                <Icon type='circle-notch' size="lg" spin style={{fontSize: '40px', margin: 'auto', display: 'block', color: 'var(--primary)'}}/>
                <div style={{margin: 'auto', marginTop: '5px', fontSize: '22px'}}>{status}</div>
              </div>
            }
            <Tabs
              type="card"
              animated={false}
              defaultActiveKey="1"
              destroyInactiveTabPane={false}
              tabBarStyle={{ width: '100%', }}
              style={{height: '100%'}}
              items={[
                {
                  label: <div>
                            <Icon type='list' size="lg" style={{marginRight: '10px'}}/>
                              Profile
                          </div>,
                  key: '1',
                  children: (
                    <div style={{height: '100%'}}>
                      <ProfileProperties workingFolder={importFolder.id} setCurrentValues={setCurrentPropertyValues} />
                    </div>
                  ),
                },
                {
                  label: <div>
                            <Icon type='envelope' size="lg" style={{marginRight: '10px'}}/>
                              Notifications
                          </div>,
                  key: '2',
                  forceRender: true,
                  children: <NotificationForm />,
                },
                {
                  label: <div>
                            <Icon type='cogs' size="lg" style={{marginRight: '10px'}}/>
                              Workflow
                          </div>,
                  key: '3',
                  forceRender: true,
                  children: <WorkflowForm workingFolder={importFolder.id} folderSettings={folderSettings} />,
                },
                {
                  label: <div>
                            <Icon type='sliders-h' size="lg" style={{marginRight: '10px'}}/>
                              Advanced
                          </div>,
                  key: '4',
                  forceRender: true,
                  children: <AdvancedOptionsForm workingFolder={importFolder.id} showAutoName={true} showZipOptions={true} folderSettings={folderSettings}/>,
                }
              ]}
            />
          </div>
        </Container>
        </Dropzone>
        <ButtonContainer>
          <Button
            type="primary"
            style={{ float: 'right', marginLeft: '10px' }}
            loading={loading}
            disabled={disabled}
            form="mainForm"
            htmlType="submit"
          >
            Import File{importFiles?.length > 1 && 's'}
          </Button>
          <Button
            style={{ float: 'right' }}
            onClick={onClose}
            >
            Cancel
          </Button>
        </ButtonContainer>
      </Form.Provider>
    </Modal>
  );
};

const Container = styled.div`
  .ant-tabs-nav {
    margin-bottom: 0;
  }
  .ant-tabs-tab:first-child {
    border-left: 1px solid #f0f0f0 !important;
  }
  .ant-form-item {
    margin: 5px 0;
  }
  .ant-input-group-addon {
    padding: 0;
  }
  .ant-checkbox + span {
    padding-right: 0px;
  }
  .ant-drawer-header {
    padding: 12px 12px;
    border-bottom: none;
  }
  .ant-drawer-body {
    overflow: hidden;
  }
  .ant-table-thead > tr > th {
    background-color: #fff;
    border-top: 1px solid #f0f0f0;
  }
  .ant-form {
    height: 100%;
  }
  .ant-tabs-content-holder {
    padding: 15px;
  }
  .ant-input-group-addon {
    background-color: #fff;
  }
  .flex-item .ant-row {
    display: block;
    height: 100%;
  }
  .flex-item .ant-row div:nth-child(2) {
    height: calc(100% - 10px);
  }
  .ant-list-empty-text {
    display: none;
  }
  .flex-item-list .ant-row div:nth-child(2) {
    height: calc(100% - 25px);
  }
  .flex-item > .ant-row > div:nth-child(2) div:not(.file-icon-small) {
    height: 100%;
  }
`

const ProcessingOverlay = styled.div`
  height: 100%;
  width: 100%;
  background-color: #fff;
  opacity: .6;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 9;
`

const ButtonContainer = styled.div`
  position: absolute;
  bottom: 20px;
  right: 25px;
`
const mapStateToProps = (state) => {
  const { maxFileSize } = state.app;
  return { maxFileSize };
};

export default connect(mapStateToProps, null)(Import);